import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { SparklesIcon, ArrowRightIcon, CameraIcon, SearchIcon } from 'lucide-react';
export function ProductsPage() {
  usePageSEO({
    canonical: '/products',
    keywords: 'Shopify products, Shopify apps, ecommerce tools, FlowBoost, AI Photoshoot',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Products',
      path: '/products'
    }]),
    title: 'Products | Design Flow Digitals',
    description: 'Discover premium Shopify apps and tools by Design Flow Digitals. FlowBoost speed optimization, AI Photoshoot, and custom-built ecommerce solutions.'
  });
  return <div className="pt-32 pb-20" data-id="element-1208" data-mp="2652yzv630xp9">
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-24" data-id="element-1209" data-mp="2641llk1lfz4d">
        <h1 className="text-5xl md:text-7xl font-bold mb-8" data-id="element-1210" data-mp="2b3milox7dxeg">Our Products</h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed" data-id="element-1211" data-mp="2sf94rmqov0py">
          Smart Shopify apps built to accelerate your ecommerce growth.
        </p>
      </section>

      <div className="flex flex-col gap-16 max-w-6xl mx-auto px-6" data-id="element-1212" data-mp="2jnqrbjl4mz0k">
        {/* Category Cards */}
        <div className="grid grid-cols-1 gap-8 max-w-xl mx-auto w-full" data-id="element-1213" data-mp="2ep5mf6uxjr5s">
          {/* Apps Category */}
          <div className="group glass-card p-10 border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 relative overflow-hidden flex flex-col h-full" data-id="element-1214" data-mp="29qcbnm712e9b">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-[80px] pointer-events-none" data-id="element-1215" data-mp="24t2pcfexqipp" />

            <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-8 border border-[#D4AF37]/20" data-id="element-1216" data-mp="1vqbcqcabpgwh">
              <SparklesIcon className="w-8 h-8 text-[#D4AF37]" data-id="element-1217" data-mp="3v1uk4mfzah41" />
            </div>

            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1218" data-mp="3q3t121ijyhsk">Shopify Apps</h2>
            <p className="text-gray-400 mb-8 leading-relaxed flex-grow" data-id="element-1219" data-mp="d3jq0cc68ybj">
              Powerful tools to help merchants grow faster, automate workflows,
              and increase average order value.
            </p>

            <Link to="/apps" className="inline-flex items-center gap-2 text-white font-semibold hover:text-[#D4AF37] transition-colors" data-id="element-1220" data-mp="43emwee0gm36">
              Explore Apps{' '}
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1221" data-mp="wsc0i47a1upl" />
            </Link>
          </div>
        </div>

        {/* Featured App Highlight */}
        <div className="mt-12" data-id="element-1222" data-mp="mtw9004c8jfk">
          <div className="flex items-center gap-4 mb-8" data-id="element-1223" data-mp="mslb2gxkq5wd">
            <h3 className="text-2xl font-bold text-white" data-id="element-1224" data-mp="rrob1myp5lxh">Latest Release</h3>
            <div className="h-px bg-white/10 flex-1" data-id="element-1225" data-mp="37jbxupjzxy2k" />
          </div>

          <div className="glass-card p-8 md:p-12 border-emerald-500/30 bg-gradient-to-br from-emerald-900/10 to-transparent relative overflow-hidden group" data-id="element-1226" data-mp="37i0wsxhxr4f5">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px] pointer-events-none" data-id="element-1227" data-mp="34y4hoa2mrf2p" />

            <div className="flex flex-col md:flex-row gap-10 items-center relative z-10" data-id="element-1228" data-mp="2zzb0o7o4x5xs">
              <div className="w-24 h-24 shrink-0 rounded-2xl bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.2)]" data-id="element-1229" data-mp="3dyaojd3tpgsw">
                <SearchIcon className="w-12 h-12 text-emerald-400" data-id="element-1230" data-mp="2lbyfik28p27x" />
              </div>

              <div className="flex-1 text-center md:text-left" data-id="element-1231" data-mp="2ha4h6ifioaax">
                <div className="flex flex-col md:flex-row items-center gap-3 mb-3" data-id="element-1232" data-mp="2h7icqrnxaawx">
                  <h3 className="text-3xl font-bold text-white" data-id="element-1233" data-mp="2v2t3bsv4w9vh">SEO Suite</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider" data-id="element-1234" data-mp="2q8gh5b29wkg1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-1235" data-mp="1xksyi1kkc3w1" />{' '}
                    Latest Release
                  </span>
                </div>

                <p className="text-xl text-gray-300 mb-6 leading-relaxed" data-id="element-1236" data-mp="2g87dg7offycd">
                  The all-in-one SEO solution for Shopify. Automate your
                  technical SEO, optimize images, and dominate search rankings.
                </p>

                <Link to="/seo-suite" className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-bold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors shadow-[0_0_20px_rgba(16,185,129,0.3)]" data-id="element-1237" data-mp="2pys8xm8t0mr2">
                  View App Details <ArrowRightIcon className="w-4 h-4" data-id="element-1238" data-mp="33unkf70vivkf" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}